/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  DeductionType,
  MonthRecord,
  YearType,
  fetchDeductionMaster,
  fetchNpsRequests,
} from "./annualNps.service";

const AnnualNps: React.FC = () => {
  const [yearType, setYearType] = useState<YearType>("Calendar");
  const [year, setYear] = useState("");
  const [deductionType, setDeductionType] = useState<DeductionType>("SUPR");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [data, setData] = useState<MonthRecord[]>([]);
  const [total, setTotal] = useState({ employeeContr: "0.00", employerContr: "0.00" });

  useEffect(() => {
    fetchDeductionMaster().catch(() => undefined);
  }, []);

  const isDisabled = year.trim().length === 0;

  const handleYearChange = (value: string) => {
    setYear(value.replace(/\D/g, "").slice(0, 4));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!year.trim()) return;

    setLoading(true);
    setApiError("");
    setSubmitted(false);
    try {
      const result = await fetchNpsRequests(year.trim(), yearType, deductionType);
      setData(result.months);
      setTotal(result.total);
    } catch (err: any) {
      const message = err?.message || "Something went wrong";
      setApiError(message);
      setData([]);
      setTotal({ employeeContr: "0.00", employerContr: "0.00" });
      toast.error(message);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  const sectionLabel = `${deductionType === "NPS" ? "NPS" : "SUPR"} · ${
    yearType === "Financial" ? "Financial Year" : "Calendar Year"
  } · ${year}`;

  return (
    <div className="page-wrapper">
      <style>{npsStyles}</style>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Annual NPS/Superannuation Detail</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={all_routes.services.list}>Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Annual NPS/Superannuation Detail
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="nps-portal-banner mb-3">
          <span className="nps-portal-badge">NPS / SUPR PORTAL</span>
          <h4 className="nps-portal-title">Annual NPS/Superannuation Detail</h4>
          <p className="nps-portal-sub mb-0">Contribution Summary</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card nps-filter-card mb-3">
            <div className="card-body">
              <p className="nps-eyebrow mb-2">Year Type</p>
              <div className="nps-radio-row mb-3">
                <label className={`nps-radio ${yearType === "Financial" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="yearType"
                    checked={yearType === "Financial"}
                    onChange={() => setYearType("Financial")}
                  />
                  <span className="nps-radio-dot" />
                  Financial Year
                </label>
                <label className={`nps-radio ${yearType === "Calendar" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="yearType"
                    checked={yearType === "Calendar"}
                    onChange={() => setYearType("Calendar")}
                  />
                  <span className="nps-radio-dot" />
                  Calendar Year
                </label>
              </div>

              <div className="nps-divider" />

              <label className="nps-field-label">Year</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control nps-year-input"
                placeholder="e.g. 2024"
                maxLength={4}
                value={year}
                onChange={(e) => handleYearChange(e.target.value)}
              />

              <div className="nps-divider" />

              <label className="nps-field-label">Deduction Type (NPS / SUPR)</label>
              <div className="nps-chip-row">
                <button
                  type="button"
                  className={`nps-chip ${deductionType === "NPS" ? "selected" : ""}`}
                  onClick={() => setDeductionType("NPS")}
                >
                  NPS
                </button>
                <button
                  type="button"
                  className={`nps-chip ${deductionType === "SUPR" ? "selected" : ""}`}
                  onClick={() => setDeductionType("SUPR")}
                >
                  SUPR
                </button>
              </div>

              <div className="nps-divider" />

              <button
                type="submit"
                className="btn btn-primary w-100 nps-submit-btn"
                disabled={isDisabled || loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  "SUBMIT"
                )}
              </button>
            </div>
          </div>
        </form>

        {submitted && (
          <>
            <div className="nps-section-divider">
              <span />
              <em>{sectionLabel}</em>
              <span />
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="nps-tile nps-tile-emp">
                  <div className="nps-tile-icon">👤</div>
                  <p className="nps-tile-label">Employee Contribution</p>
                  <h5 className="nps-tile-value">₹ {total.employeeContr}</h5>
                </div>
              </div>
              <div className="col-md-6">
                <div className="nps-tile nps-tile-empr">
                  <div className="nps-tile-icon nps-tile-icon-warn">🏢</div>
                  <p className="nps-tile-label">Employer Contribution</p>
                  <h5 className="nps-tile-value nps-tile-value-warn">₹ {total.employerContr}</h5>
                </div>
              </div>
            </div>

            <div className="card nps-table-card">
              <div className="nps-table-top">
                <span className="nps-table-dot" />
                <span>Monthly Details</span>
              </div>
              <div className="table-responsive">
                {apiError ? (
                  <div className="nps-empty">
                    <div className="nps-empty-icon">⚠️</div>
                    <p className="mb-0">{apiError}</p>
                  </div>
                ) : data.length > 0 ? (
                  <table className="table nps-table mb-0">
                    <thead>
                      <tr>
                        <th className="text-start">Month</th>
                        <th className="text-end">Employee Contribution</th>
                        <th className="text-end">Employer Contribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item, index) => (
                        <tr key={`${item.month}-${index}`} className={index % 2 === 0 ? "even" : "odd"}>
                          <td className="text-start nps-month">{item.month}</td>
                          <td className="text-end">{item.employeeContr}</td>
                          <td className="text-end">{item.employerContr}</td>
                        </tr>
                      ))}
                      <tr className="nps-total-row">
                        <td className="text-start fw-bold">Total</td>
                        <td className="text-end fw-bold">{total.employeeContr}</td>
                        <td className="text-end fw-bold">{total.employerContr}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <div className="nps-empty">
                    <div className="nps-empty-icon">📭</div>
                    <p className="mb-0">No records found for {year}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!submitted && (
          <div className="nps-hint">
            <h6>How to use</h6>
            <p className="mb-0">
              Select year type (Financial or Calendar), enter a 4-digit year (e.g. 2024), choose
              deduction type (NPS / SUPR), then click SUBMIT to view your monthly contribution
              breakdown.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const npsStyles = `
.nps-portal-banner {
  background: var(--primary, #A93226);
  border-radius: 16px;
  padding: 22px 24px;
  color: #fff;
}
.nps-portal-badge {
  display: inline-block;
  background: rgba(255,255,255,0.92);
  color: var(--primary, #A93226);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  border-radius: 8px;
  padding: 3px 10px;
  margin-bottom: 10px;
}
.nps-portal-title { color: #fff; font-weight: 700; margin-bottom: 4px; }
.nps-portal-sub { color: rgba(255,255,255,0.82); font-size: 13px; }
.nps-filter-card { border: 1px solid #eee; box-shadow: 0 2px 12px rgba(0,0,0,0.04); border-radius: 16px; }
.nps-eyebrow, .nps-field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 8px;
  display: block;
}
.nps-radio-row { display: flex; gap: 24px; flex-wrap: wrap; }
.nps-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  color: #4b5563;
}
.nps-radio input { display: none; }
.nps-radio-dot {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid #9ca3af; position: relative;
}
.nps-radio.active { color: var(--primary, #A93226); font-weight: 700; }
.nps-radio.active .nps-radio-dot { border-color: var(--primary, #A93226); }
.nps-radio.active .nps-radio-dot::after {
  content: ""; position: absolute; inset: 3px; border-radius: 50%;
  background: var(--primary, #A93226);
}
.nps-divider { height: 1px; background: #eee; margin: 16px 0; }
.nps-year-input {
  max-width: 280px;
  font-weight: 700;
  letter-spacing: 2px;
  font-size: 16px;
}
.nps-chip-row { display: flex; gap: 10px; }
.nps-chip {
  flex: 1;
  height: 44px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: #f3f4f6;
  font-weight: 600;
  color: #4b5563;
}
.nps-chip.selected {
  background: rgba(169, 50, 38, 0.1);
  border-color: var(--primary, #A93226);
  color: var(--primary, #A93226);
  font-weight: 700;
}
.nps-submit-btn { height: 46px; letter-spacing: 1px; font-weight: 700; }
.nps-section-divider {
  display: flex; align-items: center; gap: 12px; margin: 8px 0 16px;
}
.nps-section-divider span { flex: 1; height: 1px; background: #e5e7eb; }
.nps-section-divider em {
  font-style: normal; font-size: 11px; font-weight: 700;
  letter-spacing: 1.2px; color: #6b7280; white-space: nowrap;
}
.nps-tile {
  background: #fff; border: 1px solid #eee; border-radius: 16px;
  padding: 16px; border-left: 3px solid var(--primary, #A93226);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04); height: 100%;
}
.nps-tile-empr { border-left-color: #f59e0b; }
.nps-tile-icon {
  width: 34px; height: 34px; border-radius: 8px;
  background: rgba(169, 50, 38, 0.12);
  display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
}
.nps-tile-icon-warn { background: rgba(245, 158, 11, 0.15); }
.nps-tile-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.6px;
  text-transform: uppercase; color: #6b7280; margin-bottom: 6px;
}
.nps-tile-value { font-weight: 700; color: var(--primary, #A93226); margin: 0; }
.nps-tile-value-warn { color: #d97706; }
.nps-table-card { border: 1px solid #eee; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
.nps-table-top {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; background: rgba(169, 50, 38, 0.08);
  border-bottom: 1px solid #eee; font-size: 11px; font-weight: 700;
  letter-spacing: 1px; text-transform: uppercase; color: var(--primary, #A93226);
}
.nps-table-dot { width: 8px; height: 8px; border-radius: 4px; background: var(--primary, #A93226); }
.nps-table thead th {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px;
  color: #6b7280; font-weight: 700; background: #fff;
}
.nps-table td { font-size: 13px; color: #374151; }
.nps-table .even { background: #f9fafb; }
.nps-table .odd { background: #fff; }
.nps-month { font-weight: 700; color: var(--primary, #A93226); }
.nps-total-row { background: #fef3c7 !important; color: #b45309; }
.nps-empty { text-align: center; padding: 36px 16px; color: #6b7280; }
.nps-empty-icon { font-size: 28px; margin-bottom: 8px; }
.nps-hint {
  border: 1px solid #eee; border-left: 3px solid var(--primary, #A93226);
  border-radius: 16px; padding: 16px; background: #fff;
}
.nps-hint h6 { color: var(--primary, #A93226); font-weight: 700; margin-bottom: 8px; }
.nps-hint p { color: #6b7280; font-size: 14px; line-height: 1.6; }
`;

export default AnnualNps;
